import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Statuses } from '@vendasta/campaigns';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CampaignSelectorService } from '../../../../data-services/campaign-selector.service';
import { UsersService } from '../../../../data-services/users.service';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using CommonDisplayActionService
 * nor CommonDisplayActionService. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class PauseCampaignForUserService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private readonly campaignsService: CampaignSelectorService,
    private readonly usersService: UsersService,
    protected readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const inputData = activity?.inputData ? JSON.parse(activity.inputData) : {};

    const campaign$ = this.campaignsService.get(activityData?.campaign_id);
    const userName$ = !inputData?.user_id
      ? of(this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_FOR_USER.THE_USER'))
      : this.usersService.getUsersById([inputData?.user_id]).pipe(
          take(1),
          map((users) => this.usersService.getUserDisplay(users, users[0].userId)),
        );

    return combineLatest([campaign$, userName$]).pipe(
      map(([campaign, userName]) => {
        if (campaign) {
          this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.ACTIVITY_LINKED', {
            campaignId: campaign.id,
            campaignName: campaign.name,
            userName: userName,
          });
        }
        return this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.ACTIVITY_DEFAULT',
          {
            userName: userName,
          },
        );
      }),
    );
  }

  getTemplateActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step?.data || '') : {};

    if (!data.campaign_id) {
      this.errorHandler.setValidationError({
        stepId: step?.id || '',
        taskDefinitionId: step?.taskDefinitionId || '',
        errorMessage: this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.CONFIG_ERROR',
        ),
        isNonDismissable: true,
      });
      return this.getActionSubtitleEmptyState(step);
    }

    return this.campaignsService.get(data?.campaign_id).pipe(
      switchMap((campaign) => {
        if (campaign?.status === Statuses.STATUSES_PUBLISHED || campaign?.status === Statuses.STATUSES_ACTIVE) {
          return this.getActionSubtitleSuccessState(campaign);
        }
        return this.getActionSubtitleErrorState(campaign, step);
      }),
    );
  }

  getActionSubtitleSuccessState(campaign: any): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.ACTION_LINKED', {
      campaignId: campaign.id,
      campaignName: campaign.name,
    });
  }

  getActionSubtitleErrorState(campaign: any, step: WorkflowStepInterface): Observable<string> {
    let errorMessage = this.translateService.instant(
      'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.CONFIG_ERROR',
    );
    if (campaign?.status === 'archived') {
      errorMessage = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.COMMON.CONFIG_ERROR.ARCHIVED_ERROR',
      );
    }
    if (campaign?.status === 'draft') {
      errorMessage = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.COMMON.CONFIG_ERROR.DRAFT_ERROR',
      );
    }

    this.errorHandler.setValidationError({
      stepId: step?.id || '',
      taskDefinitionId: step?.taskDefinitionId || '',
      errorMessage: errorMessage,
      isNonDismissable: true,
    });
    // TODO: add different text based on whether its a draft or archived?
    if (campaign) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.ACTION_LINKED', {
        campaignId: campaign.id,
        campaignName: campaign.name,
      });
    }

    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.ACTION_DEFAULT');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_USER.ACTION_DEFAULT');
  }
}
